import type { SQLiteDatabase } from 'expo-sqlite';

import { newId, now } from '@/db/ids';
import type { SystemReminderRow } from '@/db/types';
import {
  cancelSystemReminder as cancelLegacyPrimary,
  ensureAndroidNotificationChannel,
  scheduleSystemReminder as scheduleLegacyPrimary,
} from '@/lib/notifications';
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';

/**
 * Multi-reminder API for systems. The legacy `system.reminder_at` +
 * `system.reminder_label` columns still work as the "primary reminder"
 * (notification id: `homestead-system-${systemId}`) — this table layers
 * additional reminders on top, each with its own id
 * (`homestead-sysrem-${reminderId}`) so they can be cancelled
 * independently.
 */
const NOTIFICATIONS_AVAILABLE = Platform.OS !== 'web';

export interface NewSystemReminderInput {
  systemId: string;
  label: string;
  fire_at: number;
}

export async function listSystemReminders(
  db: SQLiteDatabase,
  systemId: string
): Promise<SystemReminderRow[]> {
  return await db.getAllAsync<SystemReminderRow>(
    `SELECT * FROM system_reminder
     WHERE system_id = ? AND deleted_at IS NULL
     ORDER BY fire_at ASC`,
    systemId
  );
}

export async function addSystemReminder(
  db: SQLiteDatabase,
  input: NewSystemReminderInput
): Promise<string> {
  const id = newId();
  const t = now();
  await db.runAsync(
    `INSERT INTO system_reminder (id, system_id, label, fire_at, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?)`,
    id,
    input.systemId,
    input.label.trim(),
    input.fire_at,
    t,
    t
  );
  await scheduleReminder(id, input.label, input.fire_at);
  return id;
}

export async function deleteSystemReminder(
  db: SQLiteDatabase,
  id: string
): Promise<void> {
  const t = now();
  await db.runAsync(
    `UPDATE system_reminder SET deleted_at = ?, updated_at = ? WHERE id = ?`,
    t,
    t,
    id
  );
  await cancelReminder(id);
}

/** Cancel reminders belonging to a system (called on system delete). */
export async function cancelAllForSystem(
  db: SQLiteDatabase,
  systemId: string
): Promise<void> {
  const rows = await db.getAllAsync<{ id: string }>(
    `SELECT id FROM system_reminder WHERE system_id = ? AND deleted_at IS NULL`,
    systemId
  );
  await Promise.all(rows.map((r) => cancelReminder(r.id)));
  // Also cancel the legacy "primary" reminder if any.
  await cancelLegacyPrimary(systemId).catch(() => {});
}

/** Re-export the legacy primary scheduler so call sites don't import both. */
export const schedulePrimarySystemReminder = scheduleLegacyPrimary;

async function scheduleReminder(id: string, label: string, fireAt: number) {
  if (!NOTIFICATIONS_AVAILABLE) return;
  if (fireAt <= Date.now()) return;
  const identifier = `homestead-sysrem-${id}`;
  await Notifications.cancelScheduledNotificationAsync(identifier).catch(() => {});
  try {
    await ensureAndroidNotificationChannel();
    await Notifications.scheduleNotificationAsync({
      identifier,
      content: { title: 'System reminder', body: label, data: { id, kind: 'system-reminder' } },
      trigger: {
        type: Notifications.SchedulableTriggerInputTypes.DATE,
        date: new Date(fireAt),
      },
    });
  } catch {
    // Permission denied / scheduler busy — best effort.
  }
}

async function cancelReminder(id: string) {
  if (!NOTIFICATIONS_AVAILABLE) return;
  const identifier = `homestead-sysrem-${id}`;
  await Notifications.cancelScheduledNotificationAsync(identifier).catch(() => {});
}
